import { memo } from "react";
import { Handle, Position, type NodeProps, type Node } from "@xyflow/react";
import { Crosshair } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ProgressBar } from "@/components/shared/progress-bar";
import { getStatusLabel, getStatusVariant, getLevelLabel } from "@/lib/status-machine";
import { NodeExpandToggle } from "./node-expand-toggle";
import type { ObjectiveStatus, ObjectiveLevel } from "@/types/entities";

export interface ObjectiveNodeData extends Record<string, unknown> {
  objectiveId: string;
  title: string;
  ownerName: string | null;
  level: ObjectiveLevel | null;
  status: ObjectiveStatus | null;
  completionPercent: number | null;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onNavigate: (id: string) => void;
  onFocusNode: (id: string) => void;
  onShowDetail: ((id: string) => void) | null;
}

export type ObjectiveNodeType = Node<ObjectiveNodeData, "objective">;

const STATUS_BORDER_COLORS: Record<ObjectiveStatus, string> = {
  draft: "border-[hsl(var(--secondary))] border-dashed",
  approved: "border-[hsl(var(--foreground))]",
  in_progress: "border-[hsl(var(--info))]",
  completed: "border-[hsl(var(--accent))]",
  killed: "border-[hsl(var(--destructive))]",
};

function ObjectiveNodeComponent({ data }: NodeProps<ObjectiveNodeType>) {
  const borderClass = data.status
    ? (STATUS_BORDER_COLORS[data.status] ?? "border-[hsl(var(--border))]")
    : "border-[hsl(var(--border))]";
  const pct = data.completionPercent ?? 0;

  return (
    <div
      className={`w-56 rounded-lg border-2 bg-[hsl(var(--card))] shadow-sm ${borderClass}`}
    >
      <Handle type="target" position={Position.Top} className="!bg-[hsl(var(--primary))]" />

      {/* Header with level and status */}
      <div className="flex items-center justify-between border-b px-3 py-1.5">
        <span className="text-xs font-medium text-[hsl(var(--muted-foreground))]">
          {data.level ? getLevelLabel(data.level) : "Objective"}
        </span>
        <div className="flex items-center gap-1">
          {data.status && (
            <Badge variant={getStatusVariant(data.status)} className="text-[10px] px-1.5 py-0">
              {getStatusLabel(data.status)}
            </Badge>
          )}
          <button
            type="button"
            className="rounded p-0.5 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] transition-colors"
            onClick={(e) => { e.stopPropagation(); data.onFocusNode(data.objectiveId); }}
            title="Focus on subtree"
          >
            <Crosshair className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Body — clickable to show detail (or navigate if no detail handler) */}
      <button
        type="button"
        className="w-full px-3 py-2 text-left hover:bg-[hsl(var(--muted))] transition-colors"
        onClick={() =>
          data.onShowDetail
            ? data.onShowDetail(data.objectiveId)
            : data.onNavigate(data.objectiveId)
        }
      >
        <p className="text-sm font-medium leading-tight line-clamp-2">{data.title}</p>
        {data.ownerName && (
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))] truncate">
            {data.ownerName}
          </p>
        )}
      </button>

      {/* Footer with progress and expand toggle */}
      <div className="border-t px-3 py-2">
        <div className="flex items-center gap-2">
          <ProgressBar value={pct} className="flex-1" />
          <span className="shrink-0 text-xs font-medium">
            {Math.round(pct)}%
          </span>
        </div>
        {data.hasChildren && (
          <NodeExpandToggle
            nodeId={data.objectiveId}
            isExpanded={data.isExpanded}
            onToggleExpand={data.onToggleExpand}
          />
        )}
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-[hsl(var(--primary))]" />
    </div>
  );
}

export const ObjectiveNode = memo(ObjectiveNodeComponent);
